import {Injectable} from '@angular/core';
import {UserRequest} from '../model/UserRequest';
import {Observable, ReplaySubject} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Crmurl} from '../model/crmurl';
import {Contact} from '../model/contact';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  protected crmUrl: string;
  protected crmUrlSubject = new ReplaySubject<string>();

  constructor(private http: HttpClient) {
  }

  createRequest(userRequest: UserRequest): Observable<UserRequest> {
    return this.http.post<any>(`${environment.backend}/users`, userRequest);
  }

  cleanupEditRequest(userRequest: UserRequest, contact: Contact): UserRequest {
    //Cleanup unwanted param in request, only send required fields
    // --- coords object
    userRequest.coords.firstname = userRequest.coords.firstname !== contact.firstname ? userRequest.coords.firstname : undefined;
    userRequest.coords.lastname = userRequest.coords.lastname !== contact.lastname ? userRequest.coords.lastname : undefined;
    userRequest.coords.email = userRequest.coords.email !== contact.email ? userRequest.coords.email : undefined;
    userRequest.coords.phone = userRequest.coords.phone !== contact.phone ? userRequest.coords.phone : undefined;
    // --- profile object
    const currentProfile = contact.referent ? 'Referent' : 'Simple';
    userRequest.profile.profile = userRequest.profile.profile !== currentProfile ? userRequest.profile.profile : undefined;

    return userRequest;
  }

  createEditRequest(userRequest: UserRequest, contact: Contact): Observable<UserRequest> {
    this.cleanupEditRequest(userRequest, contact);
    return this.http.post<any>(`${environment.backend}/users/${contact.crmId}`, userRequest);
  }

  listRequests(): Observable<UserRequest[]> {
    return this.http.get<UserRequest[]>(`${environment.backend}/users`);
  }

  listEditRequests(): Observable<UserRequest[]> {
    return this.http.get<UserRequest[]>(`${environment.backend}/users/edit`);
  }

  takeRequest(ur: UserRequest): Observable<UserRequest> {
    return this.http.post<UserRequest>(`${environment.backend}/users/take`, ur);
  }

  untakeRequest(ur: UserRequest): Observable<UserRequest> {
    return this.http.post<UserRequest>(`${environment.backend}/users/untake`, ur);
  }

  acceptRequest(ur: UserRequest): Observable<UserRequest> {
    return this.http.post<UserRequest>(`${environment.backend}/users/accept`, ur);
  }

  partialAcceptRequest(ur: UserRequest): Observable<UserRequest> {
    return this.http.post<UserRequest>(`${environment.backend}/users/partialaccept`, ur);
  }

  rejectRequest(ur: UserRequest): Observable<UserRequest> {
    return this.http.post<UserRequest>(`${environment.backend}/users/reject`, ur);
  }

  enableUser(userid: string): Observable<void> {
    return this.http.post<void>(`${environment.backend}/users/${userid}/enable`, {});
  }

  disableUser(userid: string): Observable<void> {
    return this.http.delete<void>(`${environment.backend}/users/${userid}/disable`, {});
  }

  removeUser(userid: string, definitive: boolean = true): Observable<void> {
    return this.http.delete<void>(`${environment.backend}/users/${userid}`, {
      params: {
        definitive
      }
    });
  }

  getCrmUrl(): Observable<string> {
    if (this.crmUrl == null) {
      this.http.get<Crmurl>(`${environment.backend}/users/crm`).subscribe(c => {
        this.crmUrl = c.url;
        this.crmUrlSubject.next(this.crmUrl);
        this.crmUrlSubject.complete();
      });
    }
    return this.crmUrlSubject.asObservable();
  }

  listContacts(): Observable<Contact[]> {
    return this.http.get<Contact[]>(`${environment.backend}/users/list`);
  }

  enableUserAsAdmin(c: Contact): Observable<Contact> {
    return this.http.post<Contact>(`${environment.backend}/users/enable`, c);
  }

  disableUserAsAdmin(c: Contact): Observable<Contact> {
    return this.http.post<Contact>(`${environment.backend}/users/disable`, c);
  }
}
